import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

/*
  The customer's landing page for the Confirm / Report an Issue buttons in a service
  email (Batch184).

  PUBLIC - mounted outside ProtectedRoute, because the reader is a customer with no
  login and no account in this system. Their authority is the token in the URL: a v4
  GUID, unguessable, addressing exactly one row. Same trust model as the public
  quotation PDF.

  Deliberately uses a bare axios call rather than the app's RTK Query apiSlice: that
  baseQuery attaches the stored JWT and funnels 401s into the sign-in redirect, which
  is exactly wrong here - an anonymous visitor would be bounced to a login screen
  instead of being able to answer. This page must never depend on auth state.

  ?r=confirm / ?r=issue pre-selects from the button they clicked, so the common case
  (confirm, no comment) is a single further tap. It is only a hint - nothing is
  submitted until they press the button on this page, because a link prefetcher
  opening the URL must never record an answer on the customer's behalf.
*/

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const INK = "#1F2A37";
const MUTED = "#667085";
const MAROON = "#7A1F2B";

const SOURCE_LABEL = {
  INSTALLATION: "installation",
  SERVICE_VISIT: "service request",
  COMPLAINT: "service request",
  CLIENT_VISIT: "visit",
};

const Shell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#F4F5F7", padding: "24px 16px", fontFamily: "Arial, Helvetica, sans-serif" }}>
    <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 1px 4px rgba(16,24,40,0.08)" }}>
      {children}
    </div>
  </div>
);

const Header = ({ title }) => (
  <div style={{ background: MAROON, color: "#fff", padding: "18px 24px", fontSize: 18, fontWeight: 700 }}>{title}</div>
);

const ServiceFeedbackPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [choice, setChoice] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/service-feedback/${token}`);
      setState({ status: "ready", data: res.data?.data ?? null, error: null });
    } catch (err) {
      setState({
        status: "error",
        data: null,
        error: err?.response?.data?.message || "This link could not be opened.",
      });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const hint = searchParams.get("r");
    if (hint === "confirm") setChoice("CONFIRMED");
    if (hint === "issue") setChoice("ISSUE");
  }, [searchParams]);

  const submit = async () => {
    if (!choice) return;
    // Mirrors the server's own rule, so the customer is told before a round trip.
    if (choice === "ISSUE" && !remarks.trim()) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/public/service-feedback/${token}`, {
        response: choice,
        remarks: remarks.trim() || null,
      });
      setDone({ ok: true, message: res.data?.message || "Thank you." });
    } catch (err) {
      setDone({ ok: false, message: err?.response?.data?.message || "Sorry - that could not be saved. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (state.status === "loading") {
    return (
      <Shell>
        <Header title="Service Feedback" />
        <div style={{ padding: 24, color: MUTED }}>Loading…</div>
      </Shell>
    );
  }

  if (state.status === "error") {
    return (
      <Shell>
        <Header title="Link not valid" />
        <div style={{ padding: 24, color: INK, lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>{state.error}</p>
          <p style={{ margin: 0, color: MUTED, fontSize: 13 }}>
            If you received this link in an email, please reply to that email instead and we will help.
          </p>
        </div>
      </Shell>
    );
  }

  const job = state.data;
  const what = SOURCE_LABEL[job?.sourceType] || "job";

  if (done) {
    return (
      <Shell>
        <Header title={done.ok ? "Thank you" : "Not saved"} />
        <div style={{ padding: 24, color: INK, lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>{done.message}</p>
          {done.ok && (
            <p style={{ margin: 0, color: MUTED, fontSize: 13 }}>You can close this page.</p>
          )}
        </div>
      </Shell>
    );
  }

  // Answered earlier - show what was recorded rather than inviting a second answer
  // that the server would refuse anyway.
  if (job?.alreadyResponded) {
    return (
      <Shell>
        <Header title="Already answered" />
        <div style={{ padding: 24, color: INK, lineHeight: 1.6 }}>
          <p style={{ marginTop: 0 }}>
            Thank you - you have already responded to this {what}
            {job.referenceNo ? ` (${job.referenceNo})` : ""}.
          </p>
          <p style={{ margin: 0, color: MUTED, fontSize: 13 }}>
            Recorded as <strong>{job.response === "CONFIRMED" ? "confirmed" : "issue reported"}</strong>
            {job.respondedDate ? ` on ${new Date(job.respondedDate).toLocaleDateString()}` : ""}.
          </p>
        </div>
      </Shell>
    );
  }

  const issueNeedsText = choice === "ISSUE" && !remarks.trim();

  return (
    <Shell>
      <Header title={job?.branchCode ? `Service Feedback · ${job.branchCode}` : "Service Feedback"} />
      <div style={{ padding: 24, color: INK, lineHeight: 1.6 }}>
        <p style={{ marginTop: 0 }}>
          {job?.customerName ? `Dear ${job.customerName},` : "Hello,"}
        </p>
        <p>
          Please confirm that your {what}
          {job?.referenceNo ? <strong> {job.referenceNo}</strong> : null} has been completed to your
          satisfaction{job?.technicianNames ? `, attended by ${job.technicianNames}` : ""}.
        </p>

        <div style={{ display: "flex", gap: 10, margin: "20px 0", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setChoice("CONFIRMED")}
            style={{
              flex: "1 1 200px", padding: "13px 18px", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer",
              border: choice === "CONFIRMED" ? "2px solid #1B7F4B" : "1px solid #D0D5DD",
              background: choice === "CONFIRMED" ? "#1B7F4B" : "#fff",
              color: choice === "CONFIRMED" ? "#fff" : INK,
            }}
          >
            Yes, it is complete
          </button>
          <button
            type="button"
            onClick={() => setChoice("ISSUE")}
            style={{
              flex: "1 1 200px", padding: "13px 18px", borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: "pointer",
              border: choice === "ISSUE" ? "2px solid #B42318" : "1px solid #D0D5DD",
              background: choice === "ISSUE" ? "#B42318" : "#fff",
              color: choice === "ISSUE" ? "#fff" : INK,
            }}
          >
            No, there is still an issue
          </button>
        </div>

        {choice && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, color: MUTED, marginBottom: 6 }}>
              {choice === "ISSUE" ? "Please tell us what is still outstanding" : "Anything you would like to add? (optional)"}
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={4}
              maxLength={2000}
              style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #D0D5DD", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!choice || issueNeedsText || submitting}
          style={{
            width: "100%", padding: "13px 18px", borderRadius: 8, fontWeight: 700, fontSize: 15, border: "none",
            background: !choice || issueNeedsText || submitting ? "#D0D5DD" : MAROON,
            color: "#fff",
            cursor: !choice || issueNeedsText || submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Sending…" : "Send my answer"}
        </button>

        {issueNeedsText && (
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "#B42318" }}>
            Please add a short note so we know what to attend to.
          </p>
        )}
      </div>
    </Shell>
  );
};

export default ServiceFeedbackPage;
